import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { AlertCircle, RefreshCw, ExternalLink, CheckCircle2, XCircle } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';

// ============================================================================
// Types — camelCase DTO from backend mapCategory() in routes/kg-readiness.js
// ============================================================================

type ReadinessTier = 'AAA' | 'AA' | 'A' | 'B' | 'C';

interface GapDetails {
  semantic_gap_pending_count?: number;
  class_a?: boolean;
  class_b?: boolean;
  class_c?: boolean;
  class_d?: boolean;
  class_e?: boolean;
  class_f?: boolean;
}

type CoverageAttr = { present_count: number; total_count: number; pct: number };

interface KGCategory {
  categoryPath: string;
  tier: ReadinessTier | null;
  overallScore: number | null;
  isReady: boolean;
  l1Score: number | null;
  l3Score: number | null;
  l5Score: number | null;
  gapDetails: GapDetails;
  productCount: number;
  customerPriorityTier: number | null;
  scoredAt: string | null;
  // D2 competitor enrichment coverage
  competitorCoveragePct: number | null;
  competitorKbeCount: number | null;
  coverageByAttr: Record<string, CoverageAttr> | null;
}

interface KGSummary {
  total: number;
  byTier: Record<ReadinessTier, number>;
  readyCount: number;
  notReadyCount: number;
  filteredTotal: number;
}

interface APIResponse {
  categories: KGCategory[];
  summary: KGSummary;
}

// GET /api/admin/kg-readiness/history — per-category readiness-score series (PRD#8 sparklines).
export interface HistoryPoint {
  date: string;
  score: number | null;
  tier: ReadinessTier | null;
}
type CategoryHistory = Record<string, HistoryPoint[]>;

// ============================================================================
// Tier config (matches CatalogHealthDashboard)
// ============================================================================

const TIER_CONFIG: Record<ReadinessTier, { bg: string; text: string; border: string; bar: string }> = {
  AAA: { bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300',  bar: 'bg-green-400'  },
  AA:  { bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300',   bar: 'bg-blue-400'   },
  A:   { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', bar: 'bg-yellow-400' },
  B:   { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', bar: 'bg-orange-400' },
  C:   { bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-300',    bar: 'bg-red-400'    },
};

const PRIORITY_TIER_LABELS: Record<number, { label: string; bg: string; text: string }> = {
  1: { label: 'T1 Live',       bg: 'bg-emerald-100', text: 'text-emerald-800' },
  2: { label: 'T2 Enrolled',   bg: 'bg-sky-100',     text: 'text-sky-800'     },
  3: { label: 'T3 Onboarding', bg: 'bg-violet-100',  text: 'text-violet-800'  },
  4: { label: 'T4 Unmapped',   bg: 'bg-gray-100',    text: 'text-gray-600'    },
};

function TierBadge({ tier }: { tier: ReadinessTier | null }) {
  if (!tier) return <span className="text-xs text-muted-foreground">—</span>;
  const cfg = TIER_CONFIG[tier];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {tier}
    </span>
  );
}

function PriorityBadge({ tier }: { tier: number | null }) {
  if (tier === null) return <span className="text-xs text-muted-foreground">—</span>;
  const cfg = PRIORITY_TIER_LABELS[tier] ?? { label: `T${tier}`, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

function ScoreBar({ score, label }: { score: number | null; label: string }) {
  if (score === null) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">—</span>
      </div>
    );
  }
  const pct = Math.round(score * 100);
  const tier: ReadinessTier = score >= 0.90 ? 'AAA' : score >= 0.75 ? 'AA' : score >= 0.60 ? 'A' : score >= 0.40 ? 'B' : 'C';
  const cfg = TIER_CONFIG[tier];
  return (
    <div className="flex flex-col gap-0.5 min-w-[60px]">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <div className="flex-1 bg-gray-100 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full ${cfg.bar}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs tabular-nums text-muted-foreground w-7 text-right">{pct}%</span>
      </div>
    </div>
  );
}

// Local inline-SVG trend sparkline (per-page pattern, as in CatalogHealthDashboard/PipelineStatus).
// Plots the category's overall-readiness score over the snapshot window; colour = first→last
// direction (green improving, red degrading). Hover title shows the exact date range + scores.
export function TrendSparkline({ points }: { points: HistoryPoint[] }) {
  const scored = points.filter((p): p is HistoryPoint & { score: number } => p.score != null);
  if (scored.length < 2) return <span className="text-xs text-muted-foreground">—</span>;
  const values = scored.map(p => p.score);
  const w = 60, h = 18, pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1; // flat series → avoid /0
  const stepX = (w - pad * 2) / (values.length - 1);
  const coords = values.map((v, i) => ({
    x: pad + i * stepX,
    y: pad + (h - pad * 2) * (1 - (v - min) / span),
  }));
  const pts = coords.map(c => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
  const first = scored[0];
  const last = scored[scored.length - 1];
  const up = last.score >= first.score;
  const lastPt = coords[coords.length - 1];
  const title = `${first.date}: ${Math.round(first.score * 100)}% → ${last.date}: ${Math.round(last.score * 100)}%`;
  return (
    <svg width={w} height={h} role="img" aria-label={`Readiness trend — ${title}`}>
      <title>{title}</title>
      <polyline
        points={pts}
        fill="none"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        className={up ? 'stroke-green-500' : 'stroke-red-500'}
      />
      <circle cx={lastPt.x} cy={lastPt.y} r={1.75} className={up ? 'fill-green-500' : 'fill-red-500'} />
    </svg>
  );
}

// ============================================================================
// Fetch
// ============================================================================

const BASE = '/api/admin/kg-readiness';

async function fetchCategories(
  siteId: string,
  opts: { tier?: string; notReady?: boolean; priorityTier?: string; limit: number; offset: number },
  signal?: AbortSignal,
): Promise<APIResponse> {
  const params = new URLSearchParams({ siteId, limit: String(opts.limit), offset: String(opts.offset) });
  if (opts.tier && opts.tier !== 'all') params.set('tier', opts.tier);
  if (opts.notReady) params.set('notReady', 'true');
  if (opts.priorityTier && opts.priorityTier !== 'all') params.set('priorityTier', opts.priorityTier);
  const res = await authFetch(`${BASE}/categories?${params}`, { signal });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (body.code === 'MIGRATION_PENDING') throw new Error('KG readiness tables not yet available — migration pending');
    throw new Error(`HTTP ${res.status}: ${body.error ?? res.statusText}`);
  }
  return res.json();
}

async function fetchHistory(siteId: string, days = 30, signal?: AbortSignal): Promise<CategoryHistory> {
  const res = await authFetch(`${BASE}/history?siteId=${siteId}&days=${days}`, { signal });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`HTTP ${res.status}: ${body.error ?? res.statusText}`);
  }
  const json = await res.json();
  // Deep-validate the 200 shape. A malformed response must FAIL LOUDLY (→ historyError) rather than
  // be coerced to an empty series (silent "no history") or crash TrendSparkline (points.filter on a
  // non-array). An explicit empty object `history: {}` IS valid (a site with no snapshots yet).
  const malformed = () => new Error('Malformed /history response: expected { history: { <path>: HistoryPoint[] } }');
  const history = json?.history;
  if (history === null || typeof history !== 'object' || Array.isArray(history)) throw malformed();
  for (const series of Object.values(history)) {
    if (!Array.isArray(series)) throw malformed(); // e.g. { path: null } or { path: {} }
    for (const p of series) {
      if (
        p === null || typeof p !== 'object' ||
        typeof (p as { date?: unknown }).date !== 'string' ||
        !(((p as { score?: unknown }).score) === null || typeof (p as { score?: unknown }).score === 'number') ||
        !(((p as { tier?: unknown }).tier) === null || typeof (p as { tier?: unknown }).tier === 'string')
      ) {
        throw malformed();
      }
    }
  }
  return history as CategoryHistory;
}

async function triggerScoring(siteId: string): Promise<{ scored: number; failed: number }> {
  const res = await authFetch(`${BASE}/score/${siteId}`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

async function triggerReenrich(siteId: string, categoryPath: string): Promise<{ ok: boolean; message: string }> {
  const res = await authFetch(
    `${BASE}/categories/${siteId}/${encodeURIComponent(categoryPath)}/competitor-reenrich`,
    { method: 'POST' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

// ============================================================================
// Coverage bar — D2 competitor enrichment coverage percentage
// ============================================================================

function CoverageBar({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-xs text-muted-foreground">—</span>;
  const pctRound = Math.round(pct * 100);
  const color = pct >= 0.8 ? 'bg-green-400' : pct >= 0.6 ? 'bg-yellow-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-1.5 min-w-[70px]">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pctRound}%` }} />
      </div>
      <span className="text-xs tabular-nums">{pctRound}%</span>
    </div>
  );
}

// ============================================================================
// CoverageDrawer — attribute-by-attribute competitor coverage drill-down
// ============================================================================

function CoverageDrawer({
  category,
  siteId,
  onClose,
  onReenrichQueued,
}: {
  category: KGCategory;
  siteId: string;
  onClose: () => void;
  onReenrichQueued: (msg: string) => void;
}) {
  const [reenriching, setReenriching] = useState(false);
  const [reenrichError, setReenrichError] = useState<string | null>(null);

  const attrs = category.coverageByAttr
    ? Object.entries(category.coverageByAttr).sort((a, b) => a[1].pct - b[1].pct)
    : [];

  async function handleReenrich() {
    setReenriching(true);
    setReenrichError(null);
    try {
      const result = await triggerReenrich(siteId, category.categoryPath);
      onReenrichQueued(result.message);
      onClose();
    } catch (e) {
      setReenrichError(e instanceof Error ? e.message : String(e));
    } finally {
      setReenriching(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div>
            <h2 className="font-semibold text-sm truncate max-w-sm" title={category.categoryPath}>
              {category.categoryPath}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Competitor coverage — {category.competitorKbeCount ?? 0} KBEs
              {category.competitorCoveragePct != null
                ? ` · ${Math.round(category.competitorCoveragePct * 100)}% overall`
                : ''}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          {attrs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No coverage data yet — run scan-coverage first.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="pb-2 font-medium">Attribute</th>
                  <th className="pb-2 font-medium w-40">Coverage</th>
                  <th className="pb-2 font-medium w-20 text-right">KBEs</th>
                </tr>
              </thead>
              <tbody>
                {attrs.map(([attr, v]) => (
                  <tr key={attr} className="border-b border-gray-50">
                    <td className="py-1.5 font-mono text-xs">{attr}</td>
                    <td className="py-1.5"><CoverageBar pct={v.pct} /></td>
                    <td className="py-1.5 text-right tabular-nums text-xs text-muted-foreground">
                      {v.present_count}/{v.total_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t shrink-0 flex items-center gap-3">
          {reenrichError && (
            <span className="text-xs text-red-600 flex-1">{reenrichError}</span>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
            <Button
              size="sm"
              onClick={handleReenrich}
              disabled={reenriching}
              className="gap-1.5"
            >
              {reenriching ? 'Queueing…' : 'Re-enrich Competitor Pool'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Summary bar
// ============================================================================

function SummaryBar({ summary, showing }: { summary: KGSummary; showing: number }) {
  const isFiltered = summary.filteredTotal < summary.total;
  const capped = summary.filteredTotal > showing;
  return (
    <div className="flex flex-wrap gap-4 text-sm">
      {isFiltered ? (
        <span className="text-muted-foreground">
          {summary.filteredTotal} matching of {summary.total} total
        </span>
      ) : (
        <span className="text-muted-foreground">{summary.total} total categories</span>
      )}
      <span className="text-green-700 font-medium">
        {summary.readyCount} {isFiltered ? 'matching ready' : 'ready'}
      </span>
      <span className="text-red-700 font-medium">
        {summary.notReadyCount} {isFiltered ? 'matching not ready' : 'not ready'}
      </span>
      {capped && (
        <span className="text-amber-700 font-medium">
          showing {showing} of {summary.filteredTotal} matching
        </span>
      )}
      <div className="flex gap-2 ml-auto flex-wrap">
        {(Object.entries(summary.byTier) as [ReadinessTier, number][]).map(([t, n]) => (
          n > 0 ? (
            <span key={t} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${TIER_CONFIG[t].bg} ${TIER_CONFIG[t].text} ${TIER_CONFIG[t].border}`}>
              {t} <span className="font-normal">{n}</span>
            </span>
          ) : null
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main dashboard
// ============================================================================

export default function KGReadinessDashboard() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();

  const [data, setData]           = useState<APIResponse | null>(null);
  const [history, setHistory]     = useState<CategoryHistory>({});
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [scoring, setScoring]     = useState(false);
  const [scoreMsg, setScoreMsg]   = useState<string | null>(null);
  const [scanning, setScanning]   = useState(false);
  const [scanMsg, setScanMsg]     = useState<string | null>(null);
  const [reenrichMsg, setReenrichMsg] = useState<string | null>(null);
  const [drillCategory, setDrillCategory] = useState<KGCategory | null>(null);

  const [filterTier, setFilterTier]         = useState<string>('all');
  const [filterNotReady, setFilterNotReady] = useState(false);
  const [filterPriority, setFilterPriority] = useState<string>('all');

  const PAGE_SIZE = 200;
  const inflightRef = useRef<AbortController | null>(null);
  const historyInflightRef = useRef<AbortController | null>(null);
  // Tracks the currently selected siteId without stale closure — updated on every
  // render so handleScore can compare the scoring siteId against the CURRENT site
  // after the async triggerScoring() resolves.
  const currentSiteIdRef = useRef(selectedSite?.id);
  currentSiteIdRef.current = selectedSite?.id;

  const load = useCallback(async () => {
    if (!selectedSite?.id) {
      // No site selected: abort any in-flight request immediately so a pending
      // site-A response cannot resolve and render after the site is cleared.
      // Also clear error/scoreMsg so stale messages from the previous site
      // do not render alongside the 'Select a site' alert.
      inflightRef.current?.abort();
      inflightRef.current = null;
      setData(null);
      setError(null);
      setScoreMsg(null);
      setLoading(false);
      return;
    }
    // Cancel any in-flight request so a stale response cannot overwrite newer state.
    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;

    setLoading(true);
    setError(null);
    setScoreMsg(null);
    // Clear stale data from any prior site/filter before the new response arrives;
    // on error this ensures the old site's rows don't remain rendered.
    setData(null);
    try {
      const result = await fetchCategories(selectedSite.id, {
        tier: filterTier !== 'all' ? filterTier : undefined,
        notReady: filterNotReady || undefined,
        priorityTier: filterPriority !== 'all' ? filterPriority : undefined,
        limit: PAGE_SIZE,
        offset: 0,
      }, controller.signal);
      if (!controller.signal.aborted) setData(result);
    } catch (e) {
      if (!controller.signal.aborted) {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [selectedSite?.id, filterTier, filterNotReady, filterPriority]);

  // Trend history is a SEPARATE, independent request from the categories table (codex #4066
  // BLOCK): it must never block the table render, so it runs on its own effect with its own
  // abort controller. A failure surfaces as a distinct warning (historyError), not a silent
  // empty series. History is per-site (not filter-scoped), so it keys only on siteId.
  const loadHistory = useCallback(async () => {
    const sid = selectedSite?.id;
    historyInflightRef.current?.abort();
    if (!sid) {
      historyInflightRef.current = null;
      setHistory({});
      setHistoryError(null);
      return;
    }
    const controller = new AbortController();
    historyInflightRef.current = controller;
    setHistory({});
    setHistoryError(null);
    try {
      const hist = await fetchHistory(sid, 30, controller.signal);
      if (!controller.signal.aborted) { setHistory(hist); setHistoryError(null); }
    } catch (e) {
      // An abort on site switch is expected — never surface it as an error.
      if (!controller.signal.aborted) {
        setHistory({});
        setHistoryError(e instanceof Error ? e.message : String(e));
      }
    }
  }, [selectedSite?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Refresh reloads BOTH the table and the (now-decoupled) trend history independently, so a
  // transient /history failure or an external scoring run doesn't leave the Trend column stale
  // (with its warning) until the user switches sites or re-scores (codex #4068 MINOR).
  const refreshAll = useCallback(() => { load(); loadHistory(); }, [load, loadHistory]);

  const handleScore = useCallback(async () => {
    const scoringSiteId = selectedSite?.id;
    if (!scoringSiteId) return;
    setScoring(true);
    setScoreMsg(null);
    setError(null);
    try {
      const result = await triggerScoring(scoringSiteId);
      // Guard: if the user switched sites while scoring was in-flight, discard
      // the result — calling the stale load() would abort the new site's fetch
      // and repopulate the dashboard with the old site's rows.
      if (currentSiteIdRef.current !== scoringSiteId) return;
      setScoreMsg(`Scoring complete: ${result.scored} categories scored, ${result.failed} failed`);
      await load();
      // Re-check AFTER the await: the user may have switched sites while load() was in-flight.
      // Firing loadHistory() from this stale closure would abort the new site's /history request
      // and could render site-A trend data against site-B categories on overlapping paths
      // (codex #4068 BLOCK). Only refresh the sparklines if we are still on the scored site.
      if (currentSiteIdRef.current !== scoringSiteId) return;
      // A new scoring run writes a fresh history snapshot — refresh the sparklines too.
      loadHistory();
    } catch (e) {
      if (currentSiteIdRef.current === scoringSiteId) {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setScoring(false);
    }
  }, [selectedSite?.id, load, loadHistory]);

  const handleScan = useCallback(async () => {
    const scanningSiteId = selectedSite?.id;
    if (!scanningSiteId) return;
    setScanning(true);
    setScanMsg(null);
    setError(null);
    try {
      const res = await authFetch(`/api/admin/kg-readiness/scan-coverage/${scanningSiteId}`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      if (currentSiteIdRef.current !== scanningSiteId) return;
      setScanMsg(`Coverage scan complete: ${json.scanned} scanned, ${json.skipped} skipped, ${json.failed} failed`);
      await load();
    } catch (e) {
      if (currentSiteIdRef.current === scanningSiteId) {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setScanning(false);
    }
  }, [selectedSite?.id, load]);

  // Priority filter is server-side; categories returned already match the filter.
  const visibleCategories = data?.categories ?? [];

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">KG Readiness</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Category-level knowledge graph readiness: research (L1), enrichment (L3), and extraction hints (L5).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refreshAll} disabled={loading || scoring} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={handleScore} disabled={scoring || loading || !selectedSite?.id} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${scoring ? 'animate-spin' : ''}`} />
            {scoring ? 'Scoring…' : 'Re-score Now'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleScan} disabled={scanning || loading || !selectedSite?.id} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning…' : 'Scan Coverage'}
          </Button>
        </div>
      </div>

      {/* Errors / status */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {scoreMsg && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{scoreMsg}</AlertDescription>
        </Alert>
      )}
      {scanMsg && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{scanMsg}</AlertDescription>
        </Alert>
      )}
      {reenrichMsg && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{reenrichMsg}</AlertDescription>
        </Alert>
      )}
      {!selectedSite && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Select a site from the header to view KG readiness scores.</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={filterTier} onValueChange={v => { setFilterTier(v); }}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Readiness tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {(['AAA', 'AA', 'A', 'B', 'C'] as ReadinessTier[]).map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterPriority} onValueChange={v => { setFilterPriority(v); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Customer priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="1">T1 Live</SelectItem>
            <SelectItem value="2">T2 Enrolled</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={filterNotReady ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterNotReady(v => !v)}
        >
          Not Ready Only
        </Button>
      </div>

      {/* Summary */}
      {data && !loading && <SummaryBar summary={data.summary} showing={visibleCategories.length} />}

      {/* Trend history load failure — distinct from "no history" so it isn't silently swallowed */}
      {data && !loading && historyError && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 flex items-center gap-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>
            Trend history unavailable ({historyError}). Readiness rows below are current; the Trend
            column is hidden until history can be loaded — this is a load error, not "no history".
          </span>
        </div>
      )}

      {/* Loading skeletons */}
      {loading && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded" />)}
        </div>
      )}

      {/* Table */}
      {!loading && data && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Priority</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="w-20">Tier</TableHead>
                  <TableHead className="w-16">Ready</TableHead>
                  <TableHead className="w-32">Overall</TableHead>
                  <TableHead className="w-20">Trend</TableHead>
                  <TableHead className="w-28">L1 Research</TableHead>
                  <TableHead className="w-28">L3 Enrichment</TableHead>
                  <TableHead className="w-28">L5 Hints</TableHead>
                  <TableHead className="w-20">Products</TableHead>
                  <TableHead className="w-28">Competitor Cov.</TableHead>
                  <TableHead className="w-24">Gaps</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleCategories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-muted-foreground py-8">
                      No categories found for the selected filters.
                    </TableCell>
                  </TableRow>
                )}
                {visibleCategories.map(cat => {
                  const gapCount = cat.gapDetails?.semantic_gap_pending_count ?? 0;
                  const gapLink = `/semantic-gaps?category=${encodeURIComponent(cat.categoryPath)}`;
                  return (
                    <TableRow key={cat.categoryPath}>
                      <TableCell>
                        <PriorityBadge tier={cat.customerPriorityTier} />
                      </TableCell>
                      <TableCell className="font-mono text-xs max-w-xs truncate" title={cat.categoryPath}>
                        {cat.categoryPath}
                      </TableCell>
                      <TableCell>
                        <TierBadge tier={cat.tier} />
                      </TableCell>
                      <TableCell>
                        {cat.isReady
                          ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                          : <XCircle className="h-4 w-4 text-red-500" />
                        }
                      </TableCell>
                      <TableCell>
                        <ScoreBar score={cat.overallScore} label="" />
                      </TableCell>
                      <TableCell>
                        <TrendSparkline points={history[cat.categoryPath] ?? []} />
                      </TableCell>
                      <TableCell>
                        <ScoreBar score={cat.l1Score} label="L1" />
                      </TableCell>
                      <TableCell>
                        <ScoreBar score={cat.l3Score} label="L3" />
                      </TableCell>
                      <TableCell>
                        <ScoreBar score={cat.l5Score} label="L5" />
                      </TableCell>
                      <TableCell className="tabular-nums text-sm">
                        {cat.productCount}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs gap-1 hover:bg-slate-100"
                          onClick={() => setDrillCategory(cat)}
                          title="View attribute-by-attribute competitor coverage"
                        >
                          <CoverageBar pct={cat.competitorCoveragePct} />
                        </Button>
                      </TableCell>
                      <TableCell>
                        {gapCount > 0 ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs gap-1 text-amber-700 hover:text-amber-900"
                            onClick={() => navigate(gapLink)}
                          >
                            {gapCount} gaps
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* No site selected */}
      {!loading && !data && !error && selectedSite && (
        <div className="text-center text-muted-foreground py-12">
          No data — run Re-score Now to generate readiness scores for this site.
        </div>
      )}

      {/* D2 competitor coverage drill-down drawer */}
      {drillCategory && selectedSite && (
        <CoverageDrawer
          category={drillCategory}
          siteId={selectedSite.id}
          onClose={() => setDrillCategory(null)}
          onReenrichQueued={msg => { setReenrichMsg(msg); }}
        />
      )}
    </div>
  );
}
